import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  type AdminScope,
  AGENCY_REGISTER_SCOPES,
  BOOKING_MUTATION_SCOPES,
  BOOKING_READ_SCOPES,
  readAdminScopeFromUser,
  scopeIsAllowed,
} from "@/lib/admin/adminScope";

interface AdminCapabilities {
  scope: AdminScope;
  isVa: boolean;
  canFinanceActions: boolean;
  canRiskActions: boolean;
  canOpsActions: boolean;
  canSupportActions: boolean;
  canAgencyOpsAccess: boolean;
  canRegisterAgency: boolean;
  canFleetActions: boolean;
  canViewBookings: boolean;
  canMutateBookings: boolean;
  hasScope: (allowedScopes: AdminScope[]) => boolean;
}

/**
 * Progressive admin RBAC.
 * - Reads optional admin_scope from JWT metadata (default: super).
 * - Scope "va": daily cockpit, guest dossiers (no bank refund), agencies, vehicles, KYC.
 * - Backend also enforces scopes for writes (see require_admin_excluding).
 */
export function useAdminCapabilities(): AdminCapabilities {
  const { user } = useAuth();

  return useMemo(() => {
    const scope = readAdminScopeFromUser(user);
    const isVa = scope === "va";

    return {
      scope,
      isVa,
      canFinanceActions: scope === "super" || scope === "finance",
      canRiskActions: scope === "super" || scope === "risk",
      canOpsActions: scope === "super" || scope === "ops" || scope === "risk",
      canSupportActions:
        scope === "super" || scope === "support" || scope === "ops",
      canAgencyOpsAccess:
        scope === "super" ||
        scope === "support" ||
        scope === "ops" ||
        scope === "fleet" ||
        scope === "va",
      canRegisterAgency: scopeIsAllowed(scope, AGENCY_REGISTER_SCOPES),
      canFleetActions:
        scope === "super" ||
        scope === "ops" ||
        scope === "support" ||
        scope === "fleet" ||
        scope === "va",
      canViewBookings: scopeIsAllowed(scope, BOOKING_READ_SCOPES),
      canMutateBookings: scopeIsAllowed(scope, BOOKING_MUTATION_SCOPES),
      hasScope: (allowedScopes: AdminScope[]) => scopeIsAllowed(scope, allowedScopes),
    };
  }, [user]);
}
